import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@bb/ui';
import { ApiError, type AdminApi, type CampaignView } from '../../../api/client.ts';
import { usePermissions } from '../../access/usePermissions.ts';
import { EditorShell } from '../../editor/EditorShell.tsx';
import { useEditorState } from '../../editor/useEditorState.ts';
import { ConversionsStep } from '../conversions/ConversionsStep.tsx';
import { ComposeStep } from './ComposeStep.tsx';
import { ReviewStep } from './ReviewStep.tsx';
import { ScheduleStep } from './ScheduleStep.tsx';
import { StepTracker } from './StepTracker.tsx';
import { draftPatch, fromServer, partOf, stepLabel, toWizardDraft, validParts, WIZARD_STEPS, type CampaignWizardDraft, type WizardIssue, type WizardStepId } from './steps.ts';
import { TargetStep } from './TargetStep.tsx';

const errorText = (error: unknown) =>
  error instanceof ApiError && error.code === 'revision_conflict'
    ? 'Someone else saved this campaign meanwhile. Reload to see their version; your changes stay until you leave.'
    : error instanceof ApiError && error.code === 'exemption_not_allowed'
      ? 'Not saved: changing exemptions needs the messages.exemptions.manage permission.'
      : error instanceof Error
        ? error.message
        : 'The changes could not be saved.';

export interface CampaignWizardProps {
  environmentId: string;
  campaign: CampaignView;
  /** Edits are saved this long after the last change when they pass the checks; 0 turns autosave off. */
  autosaveMs?: number;
  /** The server's check of the edits runs this long after the last change. */
  validateDebounceMs?: number;
}

/**
 * The campaign wizard (BB-97, CMP-002) on the campaign page: Compose → Schedule → Target → Assign conversions →
 * Review, with a step tracker above and below that shows each step's problems. Edits are saved to the working draft
 * automatically (and with Save draft); leaving with unsaved edits asks (BB-20). Review launches or updates the
 * campaign. Read-only without `campaigns.edit` or when archived.
 */
export function CampaignWizard(props: CampaignWizardProps) {
  const permissions = usePermissions();
  if (permissions.isPending) return <p aria-busy="true">Loading the campaign wizard…</p>;
  return <Wizard {...props} canEdit={permissions.has('campaigns.edit')} canPublish={permissions.has('messages.publish')} canManageExemptions={permissions.has('messages.exemptions.manage')} />;
}

function Wizard({ environmentId, campaign, autosaveMs = 3000, validateDebounceMs = 300, canEdit, canPublish, canManageExemptions }: CampaignWizardProps & { canEdit: boolean; canPublish: boolean; canManageExemptions: boolean }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const server = useMemo(() => toWizardDraft(campaign), [campaign]);
  const form = useEditorState<CampaignWizardDraft>(server);
  const draft = form.draft ?? server;
  const [step, setStep] = useState<WizardStepId>('compose');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveFailure, setSaveFailure] = useState<unknown>(null);
  const [failedDraft, setFailedDraft] = useState<CampaignWizardDraft | null>(null);
  const [saved, setSaved] = useState(false);
  const readOnly = !canEdit || campaign.status === 'archived';
  const launched = campaign.firstPublishedAt !== null;
  // The last saved state and its version: two saves in a row must not wait for the page to re-render.
  const savedRef = useRef({ draft: server, version: campaign.draftVersion, campaign });
  useEffect(() => {
    savedRef.current = { draft: server, version: campaign.draftVersion, campaign };
  }, [server, campaign]);

  const clientIssues = useMemo(() => WIZARD_STEPS.flatMap((s) => s.validate(draft)), [draft]);
  const clientErrors = clientIssues.filter((i) => i.severity === 'error');

  // The server's aggregated check of what is on screen (the parts that pass the contract), a moment after the last change.
  const parts = useMemo(() => validParts(draft), [draft]);
  const [checked, setChecked] = useState(parts);
  useEffect(() => {
    const timer = setTimeout(() => setChecked(parts), validateDebounceMs);
    return () => clearTimeout(timer);
  }, [parts, validateDebounceMs]);
  const validation = useQuery({
    queryKey: ['campaigns', environmentId, 'validation', campaign.id, campaign.draftVersion, checked],
    queryFn: () => api.validateCampaignDraft(environmentId, campaign.id, checked),
    placeholderData: keepPreviousData,
  });
  // A part the client refuses is checked by the client: the server's issues of it describe the stored version.
  const refusedParts = new Set(clientIssues.map((i) => partOf(i.path)));
  const issues: WizardIssue[] = [...(validation.data?.issues ?? []).map(fromServer).filter((i) => !refusedParts.has(partOf(i.path))), ...clientIssues];

  const save = async () => {
    if (clientErrors.length) {
      const where = [...new Set(clientErrors.map((i) => stepLabel(i.step)))].join(', ');
      const message = `Not saved: fix the problems on ${where}.`;
      setSaveError(message);
      throw new Error(message);
    }
    const current = savedRef.current;
    const body = draftPatch(draft, current.draft, current.campaign);
    if (!body) {
      form.markSaved(draft);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveFailure(null);
    try {
      const view = await api.updateCampaignDraft(environmentId, campaign.id, { draftVersion: current.version, ...body });
      const next = toWizardDraft(view);
      savedRef.current = { draft: next, version: view.draftVersion, campaign: view };
      form.markSaved(next);
      setSaved(true);
      setFailedDraft(null);
      queryClient.setQueryData(['campaigns', environmentId, 'one', campaign.id], view);
      void queryClient.invalidateQueries({ queryKey: ['campaigns', environmentId, 'revisions', campaign.id] });
    } catch (error) {
      const message = errorText(error);
      setSaveError(message);
      setSaveFailure(error);
      setFailedDraft(draft);
      throw new Error(message, { cause: error });
    } finally {
      setSaving(false);
    }
  };

  // Autosave: a moment after the last change, when the edits pass the checks (a failed save waits for the next change).
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!autosaveMs || readOnly || !form.isDirty || clientErrors.length || saving || failedDraft === draft) return;
    const timer = setTimeout(() => void saveRef.current().catch(() => undefined), autosaveMs);
    return () => clearTimeout(timer);
  }, [autosaveMs, readOnly, form.isDirty, clientErrors.length, saving, failedDraft, draft]);

  const onLaunched = (view: CampaignView) => {
    const next = toWizardDraft(view);
    savedRef.current = { draft: next, version: view.draftVersion, campaign: view };
    form.markSaved(next);
    queryClient.setQueryData(['campaigns', environmentId, 'one', campaign.id], view);
    void queryClient.invalidateQueries({ queryKey: ['campaigns', environmentId] });
  };

  const duplicate = async () => {
    const copy = await api.campaignAction(environmentId, campaign.id, 'duplicate');
    void queryClient.invalidateQueries({ queryKey: ['campaigns', environmentId] });
    await navigate({ to: '/env/$environmentId/campaigns/$campaignId', params: { environmentId, campaignId: copy.id } });
  };

  const index = WIZARD_STEPS.findIndex((s) => s.id === step);
  const previous = WIZARD_STEPS[index - 1];
  const next = WIZARD_STEPS[index + 1];
  const stepIssues = issues.filter((i) => i.step === step);
  const stepProps = { environmentId, campaign, draft, onChange: form.setDraft, issues: stepIssues, readOnly };
  const status = saving ? 'Saving…' : form.isDirty ? 'Unsaved changes' : saved ? 'All changes saved' : '';

  return (
    <div className="canvas-wizard-frame">
      <EditorShell dirty={form.isDirty} onSave={save} canSave={!readOnly} objectLabel="campaign">
        <section className="canvas-wizard" aria-label="Campaign wizard">
          <header className="canvas-wizard__head">
            <StepTracker label="Wizard steps" current={step} issues={issues} onSelect={setStep} />
            <div className="toolbar">
              <span role="status" className="hint">
                {status}
              </span>
              {!readOnly && (
                <Button variant="primary" disabled={!form.isDirty} loading={saving} onClick={() => void save().catch(() => undefined)}>
                  Save draft
                </Button>
              )}
            </div>
          </header>
          {readOnly && <p className="hint">{canEdit ? 'The campaign is archived: restore it to edit.' : 'You can view this campaign but not change it (campaigns.edit).'}</p>}
          {launched && !readOnly && <p className="hint">The campaign has been launched: edits go to a draft, and the running version stays until you launch the update in Review.</p>}
          {saveError && <p role="alert">{saveError}</p>}

          {step === 'compose' && <ComposeStep {...stepProps} launched={launched} saveError={saveFailure} />}
          {step === 'schedule' && <ScheduleStep {...stepProps} canManageExemptions={canManageExemptions} saveError={saveFailure} />}
          {step === 'target' && <TargetStep {...stepProps} />}
          {step === 'conversions' && (
            <section aria-label="Assign conversions" className="canvas-wizard__panel">
              <h2>Assign conversions</h2>
              <fieldset disabled={readOnly} className="campaign-wizard__fieldset">
                <ConversionsStep
                  environmentId={environmentId}
                  value={draft.content.conversions}
                  locked={launched}
                  saveError={saveFailure}
                  onChange={(conversions) => form.setDraft({ ...draft, content: { ...draft.content, conversions } })}
                  {...(launched && canEdit ? { onDuplicate: () => void duplicate() } : {})}
                />
              </fieldset>
            </section>
          )}
          {step === 'review' && (
            <ReviewStep
              api={api}
              environmentId={environmentId}
              campaign={campaign}
              draft={draft}
              validation={validation}
              issues={issues}
              dirty={form.isDirty}
              canPublish={canPublish}
              readOnly={readOnly}
              onSave={save}
              onGoTo={setStep}
              onLaunched={onLaunched}
            />
          )}

          <footer className="canvas-wizard__nav">
            {previous ? (
              <Button variant="ghost" onClick={() => setStep(previous.id)}>
                Back: {previous.label}
              </Button>
            ) : (
              <span />
            )}
            <StepTracker label="Wizard steps (bottom)" current={step} issues={issues} onSelect={setStep} />
            {next ? <Button onClick={() => setStep(next.id)}>Next: {next.label}</Button> : <span />}
          </footer>
        </section>
      </EditorShell>
    </div>
  );
}
